import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, 
  Heart, ChevronLeft, Repeat, Gauge
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import BreathingCircle from '@/components/BreathingCircle';
import WaveLoader from '@/components/WaveLoader';
import { getTrack, recordPlay, toggleFavorite, removeFavorite, getFavorites, recordListening } from '@/lib/api';
import { trackListen } from '@/lib/anonTracker';
import { getSegmentAtTime, getUpcomingSegments } from '@/data/syncedScripts';
import { useFeatures } from '@/contexts/FeatureContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5];

export default function PlayerPage() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const features = useFeatures();
  const { user } = useAuth();
  const listenRef = useRef(0);
  const listenIntervalRef = useRef(null);
  
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [loading, setLoading] = useState(true);
  
  const [currentSegment, setCurrentSegment] = useState(null);
  const [upcomingSegments, setUpcomingSegments] = useState([]);
  const [dbSubtitles, setDbSubtitles] = useState(null);

  useEffect(() => { loadTrack(); }, [trackId]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Listening tracking — send every 30 seconds
  useEffect(() => {
    if (isPlaying && trackId) {
      listenIntervalRef.current = setInterval(() => {
        const secs = listenRef.current;
        if (secs > 0) {
          // Always track for logged-in users (lead or registered) — admin needs the data
          if (user) {
            recordListening(trackId, secs).catch(() => {});
          }
          // Track for anonymous/all users
          trackListen(trackId, secs);
          listenRef.current = 0;
        }
      }, 30000);
    }
    return () => {
      if (listenIntervalRef.current) clearInterval(listenIntervalRef.current);
      if (listenRef.current > 0 && trackId) {
        if (user) recordListening(trackId, listenRef.current).catch(() => {});
        trackListen(trackId, listenRef.current);
        listenRef.current = 0;
      }
    };
  }, [isPlaying, trackId, user]);

  // Fetch subtitles
  useEffect(() => {
    if (!track?.audio_url) return;
    fetch(`${API_URL}/api/subtitles?track_id=${encodeURIComponent(track.audio_url)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.lines?.length > 0) setDbSubtitles(data.lines); })
      .catch(() => {});
  }, [track?.audio_url]);

  const getSyncedScriptKey = (t) => {
    if (!t?.audio_url) return t?.category || null;
    const match = t.audio_url.match(/\/audio\/([^.]+)\./);
    return match ? match[1] : t.category;
  };

  useEffect(() => {
    if (!track) return;
    if (dbSubtitles) {
      const line = dbSubtitles.find(s => currentTime >= s.start && currentTime < s.end);
      const upcoming = dbSubtitles.filter(s => s.start > currentTime).slice(0, 2);
      setCurrentSegment(line ? { text: line.text, isAffirmation: false } : null);
      setUpcomingSegments(upcoming.map(s => ({ text: s.text, isAffirmation: false })));
      return;
    }
    const scriptKey = getSyncedScriptKey(track);
    const segment = getSegmentAtTime(scriptKey, currentTime);
    const upcoming = getUpcomingSegments(scriptKey, currentTime, 2);
    setCurrentSegment(segment);
    setUpcomingSegments(upcoming.filter(s => s !== segment));
  }, [currentTime, track, dbSubtitles]);

  // Check favorites
  useEffect(() => {
    if (!features.favorites || !user) return;
    getFavorites()
      .then(res => {
        const ids = res.data?.map(t => t.id) || [];
        setIsFavorite(ids.includes(trackId));
      })
      .catch(() => {});
  }, [trackId, features.favorites, user]);

  const loadTrack = async () => {
    try {
      const res = await getTrack(trackId);
      const t = res.data;
      // Enforce category restriction for email-lead users
      if (user && user.auth_provider === 'email_lead') {
        const allowed = user.allowed_categories || ['confidence'];
        const trackCats = Array.isArray(t.categories) && t.categories.length ? t.categories : [t.category];
        const ok = trackCats.some(c => allowed.includes(c));
        if (!ok) {
          toast.error('Sign up free to unlock this category');
          navigate('/confidence/library');
          return;
        }
      }
      setTrack(t);
      recordPlay(trackId).catch(() => {});
    } catch {
      toast.error('Failed to load track');
      navigate('/library');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const newTime = audioRef.current.currentTime;
    const delta = newTime - currentTime;
    if (delta > 0 && delta < 2) listenRef.current += delta;
    setCurrentTime(newTime);
  };

  const handleLoadedMetadata = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleSeek = (v) => { if (audioRef.current) { audioRef.current.currentTime = v[0]; setCurrentTime(v[0]); } };
  const handleSkip = (s) => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + s)); };
  const handleVolumeChange = (v) => { setVolume(v[0]); setIsMuted(false); };

  const handleToggleFavorite = async () => {
    if (!user) { toast.error('Login to save favorites'); return; }
    try {
      if (isFavorite) {
        await removeFavorite(trackId);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await toggleFavorite(trackId);
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch { toast.error('Failed to update favorites'); }
  };

  const handleEnded = () => {
    if (isRepeat) {
      // Use native loop as primary (works on mobile)
      // Fallback: manually restart
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    } else {
      setIsPlaying(false);
    }
  };

  // Sync loop attribute with isRepeat state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isRepeat;
    }
  }, [isRepeat]);

  const cycleSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(playbackSpeed);
    setPlaybackSpeed(SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length]);
  };

  if (loading) {
    return <WaveLoader label="Loading track" />;
  }

  return (
    <div className="fixed inset-0 bg-zinc-950 z-50" data-testid="player-page">
      <div className="absolute inset-0">
        <img src={track?.image_url} alt="" className="w-full h-full object-cover opacity-20 blur-3xl scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/90 to-zinc-950/70" />
      </div>

      <video ref={audioRef} src={track?.audio_url} onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded}
        playsInline webkit-playsinline="true"
        style={{position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none'}} />

      <div className="relative z-10 h-full flex flex-col">
        <header className="flex items-center justify-between p-4 md:p-6">
          <button onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Now Playing</p>
          </div>
          {features.favorites ? (
            <button onClick={handleToggleFavorite}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isFavorite ? 'bg-violet-600 text-white' : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20'
              }`} data-testid="player-favorite-btn">
              <Heart className="w-5 h-5" fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
          ) : <div className="w-10" />}
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
          <div className={`transition-all duration-700 ease-in-out ${isPlaying ? 'opacity-0 scale-90 absolute pointer-events-none' : 'opacity-100 scale-100'}`}>
            <BreathingCircle isPlaying={false} size={window.innerWidth >= 1024 ? 'medium' : 'large'} currentText={null} />
          </div>

          <div className={`max-w-2xl w-full text-center transition-all duration-700 ease-in-out ${isPlaying ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute pointer-events-none'}`}
            data-testid="synced-text-container">
            <div className="min-h-[200px] flex flex-col justify-center">
              {currentSegment ? (
                <div className="animate-subtitle-text" key={currentSegment.text}>
                  <p className={`font-serif leading-relaxed ${
                    currentSegment.isAffirmation ? 'text-2xl md:text-3xl lg:text-4xl text-white font-light' : 'text-xl md:text-2xl text-zinc-300'
                  }`} data-testid="current-text">
                    {currentSegment.isAffirmation && <span className="text-violet-400 mr-3">✦</span>}
                    {currentSegment.text}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="flex space-x-2 mb-4">
                    <span className="w-3 h-3 bg-violet-400/60 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <span className="w-3 h-3 bg-violet-400/60 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                    <span className="w-3 h-3 bg-violet-400/60 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                  </div>
                  <p className="text-zinc-500 text-sm italic">Breathe and listen...</p>
                </div>
              )}
              {upcomingSegments.length > 0 && (
                <div className="mt-6 space-y-2">
                  {upcomingSegments.slice(0, 1).map((seg) => (
                    <p key={seg.start} className="text-zinc-500 text-sm line-clamp-2">{seg.text}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center px-6 mb-4">
          <h1 className="text-xl font-serif text-white mb-1">{track?.title}</h1>
          <p className="text-zinc-500 capitalize text-sm">{track?.category}</p>
        </div>

        <div className="px-6 pb-8 md:pb-12 space-y-4">
          <div className="space-y-2 max-w-2xl mx-auto">
            <Slider value={[currentTime]} max={duration || 100} step={0.1} onValueChange={handleSeek}
              className="w-full" data-testid="progress-slider" />
            <div className="flex justify-between text-xs text-zinc-500">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-6">
            {features.loop_repeat ? (
              <button onClick={() => setIsRepeat(!isRepeat)}
                className={`player-btn ${isRepeat ? 'text-violet-400' : 'text-zinc-500 hover:text-white'}`}
                data-testid="repeat-btn">
                <Repeat className="w-5 h-5" />
              </button>
            ) : <div className="w-5" />}

            <button onClick={() => handleSkip(-15)} className="player-btn text-white hover:text-zinc-300" data-testid="skip-back-btn">
              <SkipBack className="w-6 h-6" />
            </button>

            <button onClick={handlePlayPause}
              className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-zinc-900 hover:scale-105 transition-transform shadow-lg"
              data-testid="play-pause-btn">
              {isPlaying ? <Pause className="w-7 h-7" fill="currentColor" /> : <Play className="w-7 h-7 ml-1" fill="currentColor" />}
            </button>

            <button onClick={() => handleSkip(15)} className="player-btn text-white hover:text-zinc-300" data-testid="skip-forward-btn">
              <SkipForward className="w-6 h-6" />
            </button>

            {features.playback_speed ? (
              <button onClick={cycleSpeed}
                className="player-btn text-zinc-500 hover:text-white text-xs font-mono min-w-[40px]"
                data-testid="speed-btn" title="Playback speed">
                {playbackSpeed}x
              </button>
            ) : (
              <button onClick={() => setIsMuted(!isMuted)} className="player-btn text-zinc-500 hover:text-white" data-testid="mute-btn">
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            )}
          </div>

          <div className="hidden md:flex items-center justify-center gap-4 max-w-xs mx-auto">
            <VolumeX className="w-4 h-4 text-zinc-500" />
            <Slider value={[volume]} max={100} step={1} onValueChange={handleVolumeChange}
              className="flex-1" data-testid="volume-slider" />
            <Volume2 className="w-4 h-4 text-zinc-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
